import { allWidgets, extractBaseId } from "@/utils/widgets";
import type { BreakpointLayouts, WidgetFilterData } from "@/utils/DashboardLayouts";
import { effectiveOrder } from "@/shared/utils/highlightOrder";

export type { BreakpointLayouts, WidgetFilterData };

// Represents the sets of Newsquawk-owned IDs that are safe to export and re-import across accounts.
export type NewsquawkIdSets = {
  portfolioIds: Set<string>;
  headlineQueryIds: Set<string>;
  tagWatchlistIds: Set<string>;
};

export type LayoutExportData = {
  version: 1;
  exportedAt: string;
  tabTitle: string;
  enabledWidgets: string[];
  layout: BreakpointLayouts;
  widgetFilters?: Record<string, WidgetFilterData>;
};

const BREAKPOINTS = ["lg", "md", "sm", "xs", "xxs"] as const;

/**
 * Maps each filter/highlight panel section key to the backend resource type
 * whose IDs it stores. Used during export to decide how to filter each section:
 *
 * - "portfolio"      → IDs are Portfolio IDs (covers both ticker and keyword watchlists,
 *                       which are the same Portfolio type distinguished by field content)
 * - "headlineQuery"  → IDs are CategoryQuery IDs (advanced headline filters)
 * - "tagWatchlist"   → IDs are TagsWatchlist IDs (tag-based headline filters)
 * - "static"         → Values are plain strings (category names, source names) with no
 *                       ownership concept — always safe to export as-is
 *
 * Sections not listed here are unknown and get dropped during export to prevent
 * accidentally leaking user-specific data.
 */
const SECTION_RESOURCE_TYPE: Record<
  string,
  "portfolio" | "headlineQuery" | "tagWatchlist" | "static"
> = {
  "ticker-watchlists-filter": "portfolio",
  "keyword-watchlists-filter": "portfolio",
  "ticker-watchlists-highlight": "portfolio",
  "keyword-watchlists-highlight": "portfolio",
  "category-filter": "headlineQuery",
  // Combined watchlists highlight too, and a widget's highlight order can
  // name them, so a Newsquawk one is exported like its filter selection.
  "category-highlight": "headlineQuery",
  "tag-watchlists-filter": "tagWatchlist",
  "basic-filter": "static",
  "sources-filter": "static",
  "sources-highlight": "static",
};

const validWidgetIds = new Set(allWidgets.map((w) => w.id));

/**
 * Validates imported layout data. Returns an error message string, or null if valid.
 */
export function validateLayoutData(data: unknown): string | null {
  if (!data || typeof data !== "object") {
    return "File does not contain a valid JSON object.";
  }

  const obj = data as Record<string, unknown>;

  if (obj.version !== 1) {
    return "Unsupported layout version.";
  }

  if (
    !Array.isArray(obj.enabledWidgets) ||
    !obj.enabledWidgets.every((w) => typeof w === "string")
  ) {
    return "enabledWidgets must be an array of strings.";
  }

  if (!obj.layout || typeof obj.layout !== "object") {
    return "Missing layout object.";
  }

  const layout = obj.layout as Record<string, unknown>;

  for (const bp of BREAKPOINTS) {
    if (!Array.isArray(layout[bp])) {
      return `Layout is missing the "${bp}" breakpoint.`;
    }

    for (const item of layout[bp] as unknown[]) {
      if (!item || typeof item !== "object") {
        return `Invalid layout item in "${bp}".`;
      }
      const li = item as Record<string, unknown>;
      if (typeof li.i !== "string") {
        return `Layout item in "${bp}" is missing a valid "i" (id) field.`;
      }
      for (const numField of ["x", "y", "w", "h"] as const) {
        if (typeof li[numField] !== "number") {
          return `Layout item "${li.i}" in "${bp}" is missing numeric "${numField}".`;
        }
      }
    }
  }

  // widgetFilters is optional, but if present must be a plain object
  if (obj.widgetFilters !== undefined) {
    if (
      typeof obj.widgetFilters !== "object" ||
      obj.widgetFilters === null ||
      Array.isArray(obj.widgetFilters)
    ) {
      return "widgetFilters must be a plain object if present.";
    }
  }

  // Validate that widget base IDs exist in the registry
  for (const widgetId of obj.enabledWidgets as string[]) {
    // Accept bare IDs ("NewsquawkHeadlines") or composite ("NewsquawkHeadlines-abc123")
    if (!validWidgetIds.has(widgetId)) {
      const baseId = widgetId.split("-")[0];
      if (!validWidgetIds.has(baseId)) {
        return `Unknown widget "${widgetId}" in enabledWidgets.`;
      }
    }
  }

  return null;
}

/**
 * Strips user-owned IDs from a widget's filter/highlight selections so that
 * only Newsquawk-owned (portable across accounts) data is exported.
 *
 * For each section in the selections record:
 * - Unknown sections (not in SECTION_RESOURCE_TYPE) → dropped
 * - Static sections (e.g. "basic-filter", "sources-filter") → passed through as-is
 * - Portfolio/query/tag sections → each ID is checked against the corresponding
 *   Newsquawk-owned ID set; user-created IDs are removed
 *
 * Returns undefined if no sections survive filtering (nothing worth exporting).
 */
function filterSelectionsToNewsquawkOnly(
  selections: Record<string, string[]>,
  newsquawkIds: NewsquawkIdSets
): Record<string, string[]> | undefined {
  const result: Record<string, string[]> = {};

  for (const [section, ids] of Object.entries(selections)) {
    const resourceType = SECTION_RESOURCE_TYPE[section];
    if (!resourceType) continue; // unknown section — drop

    if (resourceType === "static") {
      result[section] = ids;
      continue;
    }

    const allowedSet =
      resourceType === "portfolio"
        ? newsquawkIds.portfolioIds
        : resourceType === "headlineQuery"
          ? newsquawkIds.headlineQueryIds
          : newsquawkIds.tagWatchlistIds;

    const filtered = ids.filter((id) => allowedSet.has(String(id)));
    if (filtered.length > 0) {
      result[section] = filtered;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function collectWidgetFilters(
  tab: {
    enabledWidgets: string[];
    widgetFilters?: Record<string, WidgetFilterData>;
  },
  newsquawkIds: NewsquawkIdSets
): Record<string, WidgetFilterData> | undefined {
  if (!tab.widgetFilters) return undefined;
  const result: Record<string, WidgetFilterData> = {};

  for (const widgetId of tab.enabledWidgets) {
    const data = tab.widgetFilters[widgetId];
    if (!data) continue;
    const entry: WidgetFilterData = {};
    if (data.filter) {
      entry.filter = filterSelectionsToNewsquawkOnly(data.filter, newsquawkIds);
    }
    if (data.highlight) {
      entry.highlight = filterSelectionsToNewsquawkOnly(
        data.highlight,
        newsquawkIds
      );
      // The widget's highlight order, over the lists that survived: the rest
      // are someone's own lists, which never leave their account.
      const order = effectiveOrder(entry.highlight, data.highlightOrder);
      if (order.length > 1) entry.highlightOrder = order;
    }
    if (entry.filter || entry.highlight) result[widgetId] = entry;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Builds an export object from the active tab.
 * Filters layout items to only include widgets present in enabledWidgets,
 * stripping stale entries that React Grid Layout may have persisted.
 */
export function createLayoutExportData(
  tab: {
    title: string;
    enabledWidgets: string[];
    layout: BreakpointLayouts;
    widgetFilters?: Record<string, WidgetFilterData>;
  },
  newsquawkIds?: NewsquawkIdSets
): LayoutExportData {
  const enabledSet = new Set(tab.enabledWidgets);
  const layout = {} as BreakpointLayouts;
  for (const bp of BREAKPOINTS) {
    layout[bp] = (tab.layout[bp] ?? []).filter((item) =>
      enabledSet.has(item.i)
    );
  }

  const widgetFilters = newsquawkIds
    ? collectWidgetFilters(tab, newsquawkIds)
    : undefined;

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    tabTitle: tab.title,
    enabledWidgets: tab.enabledWidgets,
    layout,
    ...(widgetFilters && { widgetFilters }),
  };
}

/**
 * Remaps all widget IDs to fresh composite IDs (BaseId-<random>).
 * Handles bare IDs (no suffix), composite IDs, and layout items
 * that may reference IDs not present in enabledWidgets.
 */
export function remapWidgetIds(data: {
  enabledWidgets: string[];
  layout: BreakpointLayouts;
  widgetFilters?: Record<string, WidgetFilterData>;
}): {
  enabledWidgets: string[];
  layout: BreakpointLayouts;
  widgetFilters?: Record<string, WidgetFilterData>;
} {
  const idMap = new Map<string, string>();

  // Only map IDs from enabledWidgets — layout-only orphans are discarded
  for (const oldId of data.enabledWidgets) {
    if (!idMap.has(oldId)) {
      const baseId = extractBaseId(oldId);
      idMap.set(oldId, `${baseId}-${Math.random().toString(36).substr(2, 9)}`);
    }
  }

  const enabledWidgets = data.enabledWidgets.map((id) => idMap.get(id)!);

  // Only keep layout items whose IDs are in enabledWidgets
  const layout = {} as BreakpointLayouts;
  for (const bp of BREAKPOINTS) {
    layout[bp] = (data.layout[bp] ?? [])
      .filter((item) => idMap.has(item.i))
      .map((item) => ({
        ...item,
        i: idMap.get(item.i)!,
      }));
  }

  // Remap widgetFilters keys
  let widgetFilters: Record<string, WidgetFilterData> | undefined;
  if (data.widgetFilters) {
    widgetFilters = {};
    for (const [oldId, filterData] of Object.entries(data.widgetFilters)) {
      const newId = idMap.get(oldId);
      if (newId) {
        widgetFilters[newId] = filterData;
      }
    }
    if (Object.keys(widgetFilters).length === 0) {
      widgetFilters = undefined;
    }
  }

  return { enabledWidgets, layout, widgetFilters };
}

export type ReadLayoutFilesResult = {
  validTabs: LayoutExportData[];
  errors: string[];
};

/**
 * Reads and validates an array of Files, returning valid tabs and per-file errors.
 */
export async function readLayoutFiles(
  files: File[]
): Promise<ReadLayoutFilesResult> {
  const validTabs: LayoutExportData[] = [];
  const errors: string[] = [];

  for (const file of files) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const error = validateLayoutData(data);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        continue;
      }
      validTabs.push(data as LayoutExportData);
    } catch {
      errors.push(`${file.name}: Invalid JSON`);
    }
  }

  return { validTabs, errors };
}

/**
 * Triggers a JSON file download in the browser.
 */
export function downloadLayoutJson(
  data: LayoutExportData,
  filename: string
): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  if (a.parentNode) a.parentNode.removeChild(a);
  URL.revokeObjectURL(url);
}
